import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import moment from "moment";
import { Dashboard } from "components";
import { client } from "utils/api";

import { getDepartmentLegendItems } from "./departmentColors";
import MonthCalendar from "./MonthCalendar";

const fetchCalendarAbsences = async (baseMonth) => {
  const { data } = await client.get("/absences/calendar", {
    params: {
      from: baseMonth.clone().startOf("month").format("YYYY-MM-DD"),
    },
  });
  return data?.data || [];
};

const MOBILE_MEDIA_QUERY = "(max-width: 767px)";

const CalendarNavArrow = ({ direction }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24px"
    height="24px"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <polygon points="0 0 24 0 24 24 0 24" />
      <path
        d="M6.70710678,15.7071068 C6.31658249,16.0976311 5.68341751,16.0976311 5.29289322,15.7071068 C4.90236893,15.3165825 4.90236893,14.6834175 5.29289322,14.2928932 L11.2928932,8.29289322 C11.6714722,7.91431428 12.2810586,7.90106866 12.6757246,8.26284586 L18.6757246,13.7628459 C19.0828436,14.1360383 19.1103465,14.7686056 18.7371541,15.1757246 C18.3639617,15.5828436 17.7313944,15.6103465 17.3242754,15.2371541 L12.0300757,10.3841378 L6.70710678,15.7071068 Z"
        fill="#000000"
        fillRule="nonzero"
        transform={
          direction === "left"
            ? "translate(12.000003, 11.999999) scale(-1, 1) rotate(-270.000000) translate(-12.000003, -11.999999)"
            : "translate(12.000003, 11.999999) rotate(-270.000000) translate(-12.000003, -11.999999)"
        }
      />
    </g>
  </svg>
);

const AbsencesCalendar = () => {
  const [baseMonth, setBaseMonth] = useState(() => moment().startOf("month"));
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_MEDIA_QUERY).matches
  );

  const months = useMemo(
    () => [baseMonth.clone(), baseMonth.clone().add(1, "month")],
    [baseMonth]
  );

  const rangeKey = baseMonth.format("YYYY-MM");

  const { data: absences = [], isLoading } = useQuery(
    ["absences-calendar", rangeKey],
    () => fetchCalendarAbsences(baseMonth)
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = (event) => setIsMobile(event.matches);

    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const legendItems = useMemo(
    () => getDepartmentLegendItems(absences),
    [absences]
  );

  const handlePrevMonth = () => {
    setBaseMonth((current) => current.clone().subtract(1, "month"));
    setActiveMonthIndex(0);
  };

  const handleNextMonth = () => {
    setBaseMonth((current) => current.clone().add(1, "month"));
    setActiveMonthIndex(0);
  };

  const navigationLabel =
    months[0].year() === months[1].year()
      ? `${months[0].format("MMMM")} — ${months[1].format("MMMM YYYY")}`
      : `${months[0].format("MMMM YYYY")} — ${months[1].format("MMMM YYYY")}`;

  return (
    <Dashboard
      header={{
        title: "Kalendarz urlopów",
        aside: (
          <div className="absences-calendar__nav">
            <button
              type="button"
              className="absences-calendar__nav-btn"
              onClick={handlePrevMonth}
              aria-label="Poprzedni miesiąc"
            >
              <CalendarNavArrow direction="left" />
            </button>
            <span className="absences-calendar__nav-label">{navigationLabel}</span>
            <button
              type="button"
              className="absences-calendar__nav-btn"
              onClick={handleNextMonth}
              aria-label="Następny miesiąc"
            >
              <CalendarNavArrow direction="right" />
            </button>
          </div>
        ),
      }}
      className="m-b-10"
    >
      <div className="absences-calendar">
        {isLoading ? (
          <p className="absences-calendar__loading">Ładowanie kalendarza...</p>
        ) : (
          <>
            {isMobile && (
              <div className="absences-calendar__tabs">
                {months.map((month, index) => (
                  <button
                    key={month.format("YYYY-MM")}
                    type="button"
                    className={[
                      "absences-calendar__tab",
                      activeMonthIndex === index &&
                        "absences-calendar__tab--active",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setActiveMonthIndex(index)}
                  >
                    {month.format("MMMM YYYY")}
                  </button>
                ))}
              </div>
            )}
            <div className="absences-calendar__months">
              {isMobile ? (
                <MonthCalendar
                  month={months[activeMonthIndex]}
                  absences={absences}
                />
              ) : (
                months.map((month) => (
                  <MonthCalendar
                    key={month.format("YYYY-MM")}
                    month={month}
                    absences={absences}
                  />
                ))
              )}
            </div>
            {legendItems.length > 0 && (
              <div className="absences-calendar__legend">
                {legendItems.map(({ key, label, color }) => (
                  <span key={key} className="absences-calendar__legend-item">
                    <span
                      className="absences-calendar__legend-swatch"
                      style={{ backgroundColor: color }}
                    />
                    {label}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Dashboard>
  );
};

export default AbsencesCalendar;
